import { useMutation, useQueryClient } from "@tanstack/react-query";

import { CUSTOMER_QUERY_KEYS } from "common/constants";
import { del, patch, post } from "common/helpers/HTTP";
import type { DetailedWorkspace, Workspace } from "common/types";

import useWorkspaceCacheUpdate from "./useWorkspaceCacheUpdate";

async function createWorkspaceRequest(name: string): Promise<Workspace> {
  return (await post("workspaces", { name })).workspace;
}

async function editWorkspaceNameRequest(id: string, name: string) {
  await patch(`workspaces/${id}`, { name });
}

async function deleteWorkspaceRequest(id: string): Promise<void> {
  await del(`workspaces/${id}`);
}

async function updateWorkspaceImageRequest(
  id: string,
  file: File,
): Promise<DetailedWorkspace> {
  const formData = new FormData();
  formData.append("file", file);

  return (await patch(`workspaces/${id}/icon`, formData)).workspace;
}

export default function useWorkspaceMutations() {
  const queryClient = useQueryClient();
  const { updateCachedWorkspaceDetails, updateCachedWorkspaceList } =
    useWorkspaceCacheUpdate();

  const { mutateAsync: createWorkspace, isPending: isCreatingWorkspace } =
    useMutation({
      mutationFn: ({ name }: { name: string }) => createWorkspaceRequest(name),
      onSuccess: (newWorkspace) => {
        queryClient.setQueryData<Workspace[]>(
          CUSTOMER_QUERY_KEYS.workspaces.base,
          (prevWorkspaces) => [...prevWorkspaces, newWorkspace],
        );
      },
    });

  const { mutateAsync: deleteWorkspace, isPending: isDeletingWorkspace } =
    useMutation({
      mutationFn: ({ id }: { id: string }) => deleteWorkspaceRequest(id),
      onSuccess: (_, { id }) => {
        queryClient.setQueryData<Workspace[]>(
          CUSTOMER_QUERY_KEYS.workspaces.base,
          (prevWorkspaces) =>
            prevWorkspaces.filter((workspace) => workspace.id !== id),
        );
      },
    });

  const { mutateAsync: editWorkspaceName, isPending: isEditingWorkspaceName } =
    useMutation({
      mutationFn: ({ id, name }: { id: string; name: string }) =>
        editWorkspaceNameRequest(id, name),
      onSuccess: (_, { id, name }) => {
        updateCachedWorkspaceDetails(id, (draftWorkspace) => {
          draftWorkspace.name = name;
        });
        updateCachedWorkspaceList(id, (draftWorkspace) => {
          draftWorkspace.name = name;
        });
      },
    });

  const {
    mutateAsync: updateWorkspaceImage,
    isPending: isUpdatingWorkspaceImage,
  } = useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) =>
      updateWorkspaceImageRequest(id, file),
    onSuccess: ({ icon_url }, { id }) => {
      updateCachedWorkspaceDetails(id, (draftWorkspace) => {
        draftWorkspace.icon_url = icon_url;
      });
      updateCachedWorkspaceList(id, (draftWorkspace) => {
        draftWorkspace.icon_url = icon_url;
      });
    },
  });

  return {
    createWorkspace,
    isCreatingWorkspace,
    deleteWorkspace,
    isDeletingWorkspace,
    editWorkspaceName,
    isEditingWorkspaceName,
    updateWorkspaceImage,
    isUpdatingWorkspaceImage,
  };
}
